import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getTestTime } from './querys';
// stopped: 제출 완료 등으로 카운팅을 멈춰야 할 때 true. 서버 동기화/tick을 모두 중단하고 '00:00:00'을 정적으로 표시한다.
// variant: 'text'(기본, 모달 안 인라인 표기) | 'box'(플로팅 종료 박스 — 정지 시 사유별 색·문구 노출).
export const RemaingTimer = React.memo(({ test_id, stopped, variant = 'text' }) => {
    const [remainingTime, setRemainingTime] = useState(null);
    const endTimeRef = useRef(null);
    const syncIntervalRef = useRef(null);
    const fetchEndTime = useCallback(async () => {
        try {
            const response = await getTestTime(test_id);
            if (response?.result?.remaining !== undefined) {
                const serverRemainingTime = response.result.remaining;
                const serverTime = Date.now(); // 서버 시간 기준으로 계산
                endTimeRef.current = serverTime + serverRemainingTime * 1000;
                setRemainingTime(serverRemainingTime);
            }
        }
        catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [test_id]);
    const updateTimer = useCallback(() => {
        if (endTimeRef.current === null)
            return;
        const now = Date.now();
        const newRemainingTime = Math.max(0, (endTimeRef.current - now) / 1000);
        setRemainingTime(newRemainingTime);
        if (newRemainingTime > 0) {
            syncIntervalRef.current = setTimeout(updateTimer, 100); // 100ms 마다 업데이트
        }
    }, []);
    useEffect(() => {
        // 정지(제출 완료) 상태면 서버 동기화/tick 모두 중단 — 카운팅을 멈추고 '00:00:00'만 표시.
        if (stopped) {
            if (syncIntervalRef.current !== null) {
                clearTimeout(syncIntervalRef.current);
            }
            return;
        }
        fetchEndTime(); // 처음 로드할 때 서버 시간 가져오기
        const intervalId = setInterval(fetchEndTime, 10000); // 10초마다 서버와 동기화
        return () => {
            clearInterval(intervalId);
            if (syncIntervalRef.current !== null) {
                clearTimeout(syncIntervalRef.current);
            }
        };
    }, [fetchEndTime, stopped]);
    useEffect(() => {
        if (!stopped && remainingTime !== null && remainingTime > 0) {
            updateTimer();
        }
        return () => {
            if (syncIntervalRef.current !== null) {
                clearTimeout(syncIntervalRef.current);
            }
        };
    }, [remainingTime, updateTimer, stopped]);
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return [hours, minutes, secs].map(v => v.toString().padStart(2, '0')).join(':');
    };
    // 플로팅 박스(variant='box') 종료 전환: 제출완료(stopped) 또는 풀이시간 종료(remaining<=0) 시 색·문구 분기.
    // - 자진 제출(stopped, 마커≠auto): 초록 '제출 완료'
    // - 시간 종료 + 자동제출(마커 auto): 회색 '풀이 종료 / 자동 제출'
    // - 시간 종료 + 미제출(답안 없이 종료 → 마커 없음): 회색 '풀이 종료 / 미제출'
    // 마커는 제출 시 DetailQuestionContainer가 기록(auto=자동제출, manual=자진). 미제출 종료는 마커가 없다.
    const timedUp = remainingTime !== null && remainingTime <= 0;
    if (variant === 'box' && (stopped || timedUp)) {
        let reason = null;
        try {
            reason = sessionStorage.getItem('mimac_submit_reason:' + test_id);
        }
        catch (e) { }
        // 자진 제출(시간 종료 전 수동 제출) — 초록 '제출 완료'
        if (stopped && reason !== 'auto') {
            return (React.createElement("div", { className: "timer-ended is-manual" },
                React.createElement("div", { className: "timer-ended-title" }, "\uC81C\uCD9C \uC644\uB8CC"),
                React.createElement("div", { className: "timer-ended-caption" }, "\uC81C\uCD9C\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.")));
        }
        // 시간 종료 — 사유별 문구 분기(색은 동일 회색).
        // auto=자동 제출됨 / timeup_unsubmitted=미제출 종료 / 그 외(마커 미확정)=중립 문구.
        // 자동제출 케이스는 종료 감지가 마커 기록보다 앞설 수 있어, 확정 전엔 미제출로 단정하지 않고 중립 표기.
        const endedCaption = reason === 'auto'
            ? '풀이 시간이 종료되어 답안이 자동 제출되었습니다.'
            : reason === 'timeup_unsubmitted'
                ? '풀이 시간이 종료되었습니다. 제출된 답안이 없습니다.'
                : '풀이 시간이 종료되었습니다.';
        return (React.createElement("div", { className: "timer-ended is-auto" },
            React.createElement("div", { className: "timer-ended-title" }, "\uD480\uC774 \uC885\uB8CC"),
            React.createElement("div", { className: "timer-ended-sub" }, "00:00:00"),
            React.createElement("div", { className: "timer-ended-caption" }, endedCaption)));
    }
    return (React.createElement("div", { className: "text-field", style: { margin: 0 } }, !stopped && remainingTime !== null && remainingTime > 0 ? formatTime(remainingTime) : '00:00:00'));
});
